import { useSyncExternalStore } from 'react'
import { listDirectoryUsers } from '../services/chat-service'
import { seedPresenceBulk } from './presence-store'
import type { ChatUser } from '../types/chat'

/**
 * Shared, cached directory of callable contacts.
 *
 * The transfer + add-people sheets used to fetch the directory inside an effect
 * whose `load` callback depended on an `excludeUserIds` array prop. Those props
 * are recreated as new array literals on every parent render (the call overlays
 * re-render once per second from the duration timer), so the effect re-fired on
 * every render — a request storm plus a "Loading contacts…" ⇄ list flicker.
 *
 * This store loads the directory ONCE, dedupes concurrent/duplicate fetches via
 * an in-flight promise, serves cached results instantly, and refreshes at most
 * once per freshness window — without ever flipping `loading` back to true while
 * cached data is on screen.
 */
type DirectoryState = {
  contacts: ChatUser[]
  loading: boolean
  error: string | null
  loadedAt: number | null
}

let state: DirectoryState = {
  contacts: [],
  loading: false,
  error: null,
  loadedAt: null,
}

const listeners = new Set<() => void>()
let inFlight: Promise<void> | null = null

/**
 * Cached directory is treated as fresh for this long. Opening a sheet within
 * the window serves entirely from cache with zero network requests.
 */
const FRESH_MS = 60_000

function emit(): void {
  for (const listener of listeners) listener()
}

function setState(patch: Partial<DirectoryState>): void {
  state = { ...state, ...patch }
  emit()
}

export function getDirectorySnapshot(): DirectoryState {
  return state
}

/**
 * Load the directory once and cache it. Concurrent callers share a single
 * fetch; a cached-and-fresh directory short-circuits with no request. A refresh
 * over existing data does not toggle the loading flag (no flicker).
 */
export async function loadDirectory(options?: { force?: boolean }): Promise<void> {
  if (inFlight) return inFlight

  const hasCache = state.loadedAt != null && state.contacts.length > 0
  const isFresh = hasCache && Date.now() - (state.loadedAt ?? 0) < FRESH_MS
  if (!options?.force && isFresh) return

  // Only surface the loading state on a cold load; a background refresh must
  // keep the existing list visible.
  if (!hasCache) setState({ loading: true, error: null })

  inFlight = (async () => {
    try {
      // Forward `force` so the manual reload bypasses the service-layer
      // directory cache (it otherwise serves a short-TTL cached roster).
      const users = await listDirectoryUsers({ force: options?.force })
      seedPresenceBulk(users)
      setState({ contacts: users, loading: false, error: null, loadedAt: Date.now() })
      void import('../features/calls/utils/car-contacts-sync').then((m) =>
        m.syncCarContactsFromDirectory(),
      )
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not load contacts'
      // Keep stale data on screen if we have it; only surface errors on a cold load.
      setState({ loading: false, error: hasCache ? null : message })
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Drop the cached directory entirely. Called on sign-out so a different user
 * signing in on the same device can never see the previous user's contacts
 * flash in from this module-level cache.
 */
export function resetDirectoryStore(): void {
  setState({ contacts: [], loading: false, error: null, loadedAt: null })
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export type UseDirectoryResult = DirectoryState & { reload: () => void }

export function useDirectory(): UseDirectoryResult {
  const snapshot = useSyncExternalStore(subscribe, getDirectorySnapshot, getDirectorySnapshot)
  return {
    ...snapshot,
    reload: () => void loadDirectory({ force: true }),
  }
}
